import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, DollarSign, MinusCircle, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { mockData } from '../data/mockData';
import { Button } from '../components/ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine, LabelList } from 'recharts';

const COLORS = {
  positive: '#059669',
  negative: '#DC2626',
  subtotal: '#3B82F6',
  total: '#C5A059',
};

export const EBITDADetailPage = ({ year }) => {
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const waterfallData = mockData.ebitdaWaterfall[year];
  const yearlyData = mockData.finance.yearly[year];
  const prevYearData = year > 2024 ? mockData.finance.yearly[year - 1] : null;

  // Transform waterfall data for visualization
  const transformedData = waterfallData.map((item, index, arr) => {
    let start = 0;
    for (let i = 0; i < index; i++) {
      if (!arr[i].isTotal && !arr[i].isSubtotal) {
        start += arr[i].value;
      }
    }
    
    const color = item.isTotal ? COLORS.total : item.isSubtotal ? COLORS.subtotal : item.value >= 0 ? COLORS.positive : COLORS.negative;
    
    return {
      ...item,
      start: item.isTotal || item.isSubtotal ? 0 : start,
      end: item.isTotal || item.isSubtotal ? item.value : start + item.value,
      color,
      displayValue: Math.abs(item.value),
    };
  });

  // Custom Waterfall Bar
  const WaterfallBar = (props) => {
    const { x, y, width, height, start, end, color, value, isTotal, isSubtotal } = props;
    
    if (isTotal || isSubtotal) {
      return (
        <rect x={x} y={y} width={width} height={height} fill={color} rx={4} />
      );
    }
    
    const barY = value >= 0 ? y : y + Math.abs(height);
    const barHeight = Math.abs(height);
    
    return (
      <rect x={x} y={barY} width={width} height={barHeight} fill={color} rx={2} />
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ebitda-detail-page">
      {/* Header */}
      <div className="bg-primary text-white px-8 py-6">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/finance')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'pt' ? 'Finanças' : 'Finance'}
            </Button>
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded hover:bg-white/20 transition-colors"
              data-testid="ebitda-language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium uppercase">{language}</span>
            </button>
          </div>
          <h1 className="font-serif text-3xl font-bold">
            {language === 'pt' ? 'Análise EBITDA' : 'EBITDA Analysis'}
          </h1>
          <p className="text-white/70 mt-2">
            {language === 'pt' ? 'Decomposição waterfall da receita ao EBITDA' : 'Waterfall breakdown from revenue to EBITDA'}
          </p>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-8 py-8 space-y-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">{language === 'pt' ? 'Receita' : 'Revenue'}</p>
            <p className="font-serif text-4xl font-semibold text-primary">{yearlyData.revenue}M</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">EBITDA</p>
            <p className="font-serif text-4xl font-semibold text-gold-600">{yearlyData.ebitda}M</p>
            {prevYearData && (
              <div className="flex items-center gap-1 mt-2 text-emerald-600">
                <TrendingUp className="w-4 h-4" />
                <span className="text-sm">+{Math.round((yearlyData.ebitda / prevYearData.ebitda - 1) * 100)}%</span>
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">{language === 'pt' ? 'Margem EBITDA' : 'EBITDA Margin'}</p>
            <p className="font-serif text-4xl font-semibold text-emerald-600">{yearlyData.ebitdaMargin}%</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">{language === 'pt' ? 'vs Ano Anterior' : 'vs Prior Year'}</p>
            <p className="font-serif text-4xl font-semibold text-primary">
              {prevYearData && prevYearData.ebitda 
                ? `${Math.round((yearlyData.ebitda / prevYearData.ebitda - 1) * 100) > 0 ? '+' : ''}${Math.round((yearlyData.ebitda / prevYearData.ebitda - 1) * 100)}%`
                : 'N/A'
              }
            </p>
          </div>
        </div>

        {/* Waterfall Chart */}
        <div className="bg-white rounded-xl border border-slate-200 p-8">
          <h3 className="font-serif text-xl font-semibold text-primary mb-6">
            EBITDA Waterfall {year} (M USD)
          </h3>
          <ResponsiveContainer width="100%" height={450}>
            <BarChart data={transformedData} margin={{ top: 30, right: 30, left: 30, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12, fill: '#64748B' }} 
                stroke="#94A3B8"
                interval={0}
                angle={-15}
                textAnchor="end"
              />
              <YAxis tick={{ fontSize: 12, fill: '#64748B' }} stroke="#94A3B8" />
              <Tooltip 
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-primary text-white rounded-lg px-4 py-3 shadow-xl">
                        <p className="font-medium">{data.name}</p>
                        <p className="text-gold-400 text-lg font-semibold">
                          {data.value >= 0 ? '+' : ''}{data.value}M
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="displayValue" shape={<WaterfallBar />}>
                {transformedData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
                <LabelList 
                  dataKey="value" 
                  position="top" 
                  formatter={(value) => `${value >= 0 ? '+' : ''}${value}M`}
                  style={{ fontSize: 11, fontWeight: 600, fill: '#0A192F' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cost Breakdown */}
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-serif text-lg font-semibold text-primary mb-4">
              {language === 'pt' ? 'Estrutura de Custos' : 'Cost Structure'}
            </h3>
            <div className="space-y-4">
              {waterfallData.filter(d => d.value < 0 && !d.isTotal && !d.isSubtotal).map((item, idx) => (
                <div key={idx}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">{item.name}</span>
                    <span className="text-sm font-semibold text-red-600">{item.value}M</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-red-500" 
                      style={{ width: `${Math.abs(item.value) / yearlyData.revenue * 100}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{(Math.abs(item.value) / yearlyData.revenue * 100).toFixed(1)}% {language === 'pt' ? 'da receita' : 'of revenue'}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-gold-50 to-amber-50 rounded-xl border border-gold-200 p-6">
            <h3 className="font-serif text-lg font-semibold text-gold-800 mb-4">
              {language === 'pt' ? 'Evolução da Margem EBITDA' : 'EBITDA Margin Evolution'}
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {[2024, 2025, 2026].map((y) => (
                <div key={y} className={`text-center p-4 rounded-lg ${y === year ? 'bg-gold-500 text-white' : 'bg-white/80'}`}>
                  <p className="font-serif text-3xl font-semibold">{mockData.finance.yearly[y].ebitdaMargin}%</p>
                  <p className={`text-sm ${y === year ? 'text-white/80' : 'text-slate-600'}`}>{y}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-gold-700 mt-4 text-center">
              {language === 'pt' 
                ? `Melhoria de ${(mockData.finance.yearly[2026].ebitdaMargin - mockData.finance.yearly[2024].ebitdaMargin).toFixed(1)}pp desde 2024`
                : `${(mockData.finance.yearly[2026].ebitdaMargin - mockData.finance.yearly[2024].ebitdaMargin).toFixed(1)}pp improvement since 2024`}
            </p>
          </div>
        </div>

        {/* Year Comparison */}
        {prevYearData && (
          <div className="bg-primary rounded-xl p-6 text-white">
            <h3 className="font-serif text-lg font-semibold mb-4">
              {language === 'pt' ? 'Comparativo EBITDA' : 'EBITDA Comparison'} {year} vs {year - 1}
            </h3>
            <div className="grid grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-white/70 text-sm mb-2">EBITDA {year - 1}</p>
                <p className="font-serif text-4xl font-semibold">{prevYearData.ebitda}M</p>
              </div>
              <div className="text-center">
                <p className="text-white/70 text-sm mb-2">EBITDA {year}</p>
                <p className="font-serif text-4xl font-semibold text-gold-400">{yearlyData.ebitda}M</p>
              </div>
              <div className="text-center">
                <p className="text-white/70 text-sm mb-2">{language === 'pt' ? 'Variação Absoluta' : 'Absolute Change'}</p>
                <p className="font-serif text-4xl font-semibold text-emerald-400">+{yearlyData.ebitda - prevYearData.ebitda}M</p>
              </div>
              <div className="text-center">
                <p className="text-white/70 text-sm mb-2">{language === 'pt' ? 'Variação %' : '% Change'}</p>
                <p className="font-serif text-4xl font-semibold text-emerald-400">+{Math.round((yearlyData.ebitda / prevYearData.ebitda - 1) * 100)}%</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EBITDADetailPage;
